import React, { useMemo, useState } from "react";
import { useData } from "../../context/DataContext";
import { useAuth } from "../../context/AuthContext";
import { cellText, getStatusBadge, getPriorityBadge, formatDate, extractEmployees, calculatePerformanceRating, normalizeToMs } from "../../utils/helpers";
import CustomSelect from "../../components/CustomSelect";


export default function AdminDashboard({ onNavigate }) {
  const { adminRecords, userRecords, loading, lastFetched } = useData();
  const { user } = useAuth();
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [barCompFilter, setBarCompFilter] = useState("all");
  const [barDeptFilter, setBarDeptFilter] = useState("all");

  const companies = useMemo(() => {
    const set = new Set();
    adminRecords.forEach(r => {
      const c = cellText(r.fields?.["Company Name"]);
      if (c && c !== "—") set.add(c);
    });
    return Array.from(set).sort();
  }, [adminRecords]);

  const departments = useMemo(() => {
    const set = new Set();
    adminRecords.forEach(r => {
      const d = cellText(r.fields?.["Assignee Department"]) || cellText(r.fields?.["Creator Department"]);
      if (d && d !== "—") set.add(d);
    });
    return Array.from(set).sort();
  }, [adminRecords]);

  const stats = useMemo(() => {
    const statuses = adminRecords.map(r => cellText(r.fields?.["Overall Status"]).toLowerCase());
    const names = new Set();
    for (const r of adminRecords) {
      const u = r.fields?.["Assigned Users"];
      if (Array.isArray(u)) u.forEach(x => { if (x?.en_name) names.add(x.en_name); else if (typeof x === "string") names.add(x); });
    }
    return {
      total: adminRecords.length,
      done: statuses.filter(s => s.includes("done")).length,
      overdue: statuses.filter(s => s.includes("overdue")).length,
      inProgress: statuses.filter(s => s.includes("progress")).length,
      notStarted: statuses.filter(s => s.includes("not started") || s.includes("⬜")).length,
      employees: names.size,
      userTasks: userRecords.length,
    };
  }, [adminRecords, userRecords]);

  const recent = useMemo(() =>
    [...adminRecords].sort((a,b) => (b.fields?.["Start Date"]||0)-(a.fields?.["Start Date"]||0)).slice(0,6),
    [adminRecords]
  );

  const ratings = useMemo(() => {
    const employees = extractEmployees(adminRecords);
    return employees.map(emp => {
      const empTasks = userRecords.filter(r => {
        const fieldVal = r.fields?.["User"] || r.fields?.["Assigned Users"] || r.fields?.["Total Assigned Users"];
        const name = emp.name.toLowerCase();
        
        const check = (v) => {
          if (!v) return false;
          if (typeof v === "string") return v.toLowerCase().includes(name);
          if (typeof v === "object") {
             return (v.en_name || v.name || "").toLowerCase().includes(name);
          }
          return false;
        };
        
        if (Array.isArray(fieldVal)) return fieldVal.some(check);
        return check(fieldVal);
      });
      return {
        name: emp.name,
        rating: calculatePerformanceRating(empTasks),
        taskCount: empTasks.length,
        doneCount: empTasks.filter(t => t.fields?.["My Completion"]).length
      };
    }).sort((a,b) => b.rating - a.rating);
  }, [adminRecords, userRecords]);

  // Status Pie Stats filtered by Company (Using Admin Table for 1-task = 1-count)
  const statusPieStats = useMemo(() => {
    const now = Date.now();
    let comp = 0, pend = 0, overd = 0, overdDone = 0;

    adminRecords.forEach(r => {
      const f = r.fields || {};
      const company = cellText(f["Company Name"]);
      if (selectedCompany !== "all" && company !== selectedCompany) return;

      const statusText = cellText(f["Overall Status"]) || "Not Started";
      const isDone = statusText.toLowerCase().includes("done") || statusText.includes("✅");
      const dueTime = normalizeToMs(f["End Date"]);
      const compTime = normalizeToMs(f["Completed At"] || f["Completion Time"]);
      const isLate = statusText.toLowerCase().includes("overdue (done)") || (isDone && compTime > (dueTime + 60000) && dueTime > 0);
      const isOverdue = statusText.toLowerCase().includes("overdue") && !isLate;

      if (isDone) {
        if (isLate) overdDone++;
        else comp++;
      } else {
        if (isOverdue) overd++;
        else pend++;
      }
    });

    return [
      { name: "Completed",      count: comp,      color: "#10B981" },
      { name: "Overdue (Done)", count: overdDone, color: "#F43F5E" },
      { name: "Pending",        count: pend,      color: "#F59E0B" },
      { name: "Overdue",        count: overd,     color: "#EF4444" },
    ].filter(s => s.count > 0);
  }, [adminRecords, selectedCompany]);
  // Grouped Bar Stats by Employee
  const { employeeDetailedStats, globalBarMax } = useMemo(() => {
    const employees = extractEmployees(adminRecords);
    const now = Date.now();
    let max = 5;
    
    const es = employees.map(emp => {
      const name = emp.name.toLowerCase();
      const empTasks = userRecords.filter(r => {
        const fieldVal = r.fields?.["User"] || r.fields?.["Assigned Users"] || r.fields?.["Total Assigned Users"];
        const check = (v) => {
          if (!v) return false;
          if (typeof v === "string") return v.toLowerCase().includes(name);
          if (typeof v === "object") return (v.en_name || v.name || "").toLowerCase().includes(name);
          return false;
        };
        if (Array.isArray(fieldVal)) return fieldVal.some(check);
        return check(fieldVal);
      });

      let total = empTasks.length, completed = 0, pending = 0, overdue = 0, withinTime = 0, afterTime = 0;

      empTasks.forEach(t => {
        const f = t.fields || {}, isDone = !!f["My Completion"];
        const dueTime = normalizeToMs(f["End Date"]), compTime = normalizeToMs(f["Completed At"] || f["Completion Time"]);
        if (isDone) {
          completed++;
          if (compTime <= (dueTime + 60000) || dueTime === 0) withinTime++;
          else afterTime++;
        } else {
          if (dueTime > now || dueTime === 0) pending++;
          else overdue++;
        }
      });
      
      const rowMax = Math.max(total, completed, pending, overdue, withinTime, afterTime);
      if (rowMax > max) max = rowMax;

      return { name: emp.name, total, completed, pending, overdue, withinTime, afterTime };
    });

    return { employeeDetailedStats: es, globalBarMax: max > 0 ? max : 5 };
  }, [adminRecords, userRecords]);

  // Grouped Bar Stats by Company (Using Admin Table)
  const { companyDetailedStats, companyBarMax } = useMemo(() => {
    const companiesList = barCompFilter === "all" ? companies : [barCompFilter];
    const now = Date.now();
    let max = 5;

    const stats = companiesList.map(compName => {
      const compTasks = adminRecords.filter(r => {
        const c = cellText(r.fields?.["Company Name"]);
        const d = cellText(r.fields?.["Assignee Department"]) || cellText(r.fields?.["Creator Department"]);
        if (c !== compName) return false;
        if (barDeptFilter !== "all" && d !== barDeptFilter) return false;
        return true;
      });

      let total = compTasks.length, completed = 0, pending = 0, overdue = 0, withinTime = 0, afterTime = 0;

      compTasks.forEach(t => {
        const f = t.fields || {};
        const statusText = cellText(f["Overall Status"]).toLowerCase();
        const isDone = statusText.includes("done") || statusText.includes("✅");
        const dueTime = normalizeToMs(f["End Date"]), compTime = normalizeToMs(f["Completed At"] || f["Completion Time"]);
        
        if (isDone) {
          completed++;
          if (compTime <= (dueTime + 60000) || dueTime === 0) withinTime++;
          else afterTime++;
        } else {
          if (statusText.includes("overdue") || (dueTime > 0 && dueTime < now)) overdue++;
          else pending++;
        }
      });

      const rowMax = Math.max(total, completed, pending, overdue, withinTime, afterTime);
      if (rowMax > max) max = rowMax;

      return { name: compName, total, completed, pending, overdue, withinTime, afterTime };
    });

    return { companyDetailedStats: stats, companyBarMax: max > 0 ? max : 5 };
  }, [adminRecords, barCompFilter, barDeptFilter, companies]);

  const COLORS = ["#4b5563", "#f59e0b", "#9333ea", "#d1d5db", "#f43f5e", "#7c3aed", "#fbcfe8", "#2dd4bf", "#78716c", "#fbbf24", "#a3a3a3", "#3f6212", "#6366f1", "#f97316"];

  if (loading && adminRecords.length === 0) return <div className="loading-box"><div className="spinner"/><span>Loading dashboard...</span></div>;

  const STAT_CARDS = [
    { label:"Total Tasks",     value:stats.total,      color:"#6366F1", icon:"📋", page:"all-tasks",   sub:"All admin tasks" },
    { label:"Avg Users/Task",  value:stats.userTasks ? (stats.userTasks / stats.total).toFixed(1) : 0, color:"#3B82F6", icon:"👤", page:"all-tasks", sub:"Overall ratio" },
    { label:"Completed",       value:stats.done,       color:"#10B981", icon:"✅", page:"completed",   sub:"Master done" },
    { label:"In Progress",     value:stats.inProgress, color:"#F59E0B", icon:"🟡", page:"pending",     sub:"Active tasks" },
    { label:"Overdue",         value:stats.overdue,    color:"#EF4444", icon:"🔴", page:"overdue",     sub:"Need action" },
    { label:"Not Started",     value:stats.notStarted, color:"#64748B", icon:"⬜", page:"pending",     sub:"Upcoming" },
  ];

  return (
    <div className="fade-in">
      <div className="welcome-banner">
        <div className="welcome-text">
          <h2>Welcome back, {user?.name?.split(" ")[0]}! 👋</h2>
          <p>Here's your team task overview {lastFetched && `· Updated ${lastFetched}`}</p>
        </div>
        <div className="welcome-emoji">📊</div>
      </div>

      <div className="stats-grid">
        {STAT_CARDS.map(s => (
          <div key={s.label} className="stat-card" style={{ borderTop:`3px solid ${s.color}`, cursor:"pointer" }} onClick={() => onNavigate(s.page)}>
            <div className="stat-card-icon" style={{ background:`${s.color}15` }}>{s.icon}</div>
            <div className="stat-card-value" style={{ color:s.color }}>{s.value}</div>
            <div className="stat-card-label">{s.label}</div>
            <div className="stat-card-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      <div className="card" style={{ margin: '1.5rem 0' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span className="card-title">📉 Task Status Overview</span>
          <CustomSelect 
            placeholder="All Companies"
            options={companies}
            value={selectedCompany === "all" ? "" : selectedCompany}
            onChange={val => setSelectedCompany(val || "all")}
            isClearable={true}
            style={{ minWidth: 200 }}
          />
        </div>
        <div style={{ padding: '2rem', display: 'flex', justifyContent: 'center' }}>
          {statusPieStats.length > 0 ? (
            <PieChart 
              title={selectedCompany === "all" ? "Overall System Status" : `${selectedCompany} Status`} 
              data={statusPieStats} 
              totalColor="var(--primary)" 
              typeLabel="Tasks" 
            />
          ) : (
            <div style={{ padding: '4rem', textAlign: 'center', color: 'var(--gray-400)' }}>
              No data available for this selection.
            </div>
          )}
        </div>
      </div>

      <div className="performance-section">
        <div className="performance-header">
          <div className="performance-title">Performance Rating</div>
          <div className="chart-legend">
            {ratings.map((r, i) => (
              <div key={r.name} className="legend-item">
                <div className="legend-dot" style={{ background: COLORS[i % COLORS.length] }}></div>
                <span>{r.rating.toFixed(0)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="chart-container">
          <div className="chart-y-axis">
            <span>10.00</span>
            <span>8.00</span>
            <span>6.00</span>
            <span>4.00</span>
            <span>2.00</span>
            <span>0.00</span>
          </div>
          
          <div className="chart-grid-wrapper">
            {[10, 8, 6, 4, 2, 0].map(v => <div key={v} className="chart-grid-line"></div>)}
          </div>

          {ratings.map((r, i) => (
            <div key={r.name} className="performance-bar-wrapper">
              <div 
                className="performance-bar" 
                title={`${r.name}\nRating: ${r.rating.toFixed(2)}/10\nTasks: ${r.doneCount}/${r.taskCount} Completed`}
                style={{ 
                  height: `${Math.max(r.rating * 10, 5)}%`, 
                  background: COLORS[i % COLORS.length] 
                }}
              >
                <div className="performance-bar-value" style={{ color: COLORS[i % COLORS.length] }}>
                  {r.rating.toFixed(2)}
                </div>
              </div>
              <div className="performance-bar-label">{r.name}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grouped-chart-card">
         <div className="performance-title" style={{ marginBottom: '1.5rem' }}>Task By Employee</div>
         <div className="chart-legend-horizontal">
            <div className="legend-item-horizontal"><div className="legend-dot" style={{ background: '#9333ea' }}></div><span>Total Tasks</span></div>
            <div className="legend-item-horizontal"><div className="legend-dot" style={{ background: '#22c55e' }}></div><span>Completed Tasks</span></div>
            <div className="legend-item-horizontal"><div className="legend-dot" style={{ background: '#eab308' }}></div><span>Pending Tasks</span></div>
            <div className="legend-item-horizontal"><div className="legend-dot" style={{ background: '#ef4444' }}></div><span>Overdue Tasks</span></div>
            <div className="legend-item-horizontal"><div className="legend-dot" style={{ background: '#06b6d4' }}></div><span>Completed Within Time</span></div>
            <div className="legend-item-horizontal"><div className="legend-dot" style={{ background: '#f97316' }}></div><span>Completed After Time</span></div>
         </div>
         <div className="grouped-bar-container">
            <div className="chart-y-axis-grouped">
               <span>{globalBarMax}</span>
               <span>{Math.round(globalBarMax * 0.75)}</span>
               <span>{Math.round(globalBarMax * 0.5)}</span>
               <span>{Math.round(globalBarMax * 0.25)}</span>
               <span>0</span>
            </div>
            <div className="grid-wrapper-grouped">
               {[1, 0.75, 0.5, 0.25, 0].map(v => <div key={v} style={{ borderTop: '1px solid #f1f5f9', width: '100%', height: 0 }}></div>)}
            </div>
            {employeeDetailedStats.map(emp => (
               <div key={emp.name} className="bar-group">
                  <GroupBar value={emp.total} color="#9333ea" max={globalBarMax} label="Total" />
                  <GroupBar value={emp.completed} color="#22c55e" max={globalBarMax} label="Completed" />
                  <GroupBar value={emp.pending} color="#eab308" max={globalBarMax} label="Pending" />
                  <GroupBar value={emp.overdue} color="#ef4444" max={globalBarMax} label="Overdue" />
                  <GroupBar value={emp.withinTime} color="#06b6d4" max={globalBarMax} label="Within" />
                  <GroupBar value={emp.afterTime} color="#f97316" max={globalBarMax} label="After" />
                  <div className="group-title-label">{emp.name}</div>
               </div>
            ))}
         </div>
      </div>

       <div className="grouped-chart-card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--gray-100)', paddingBottom: '1rem', marginBottom: '1.5rem' }}>
             <span className="card-title">🏢 Task By Company</span>
             <div className="filter-group">
            <CustomSelect 
              placeholder="All Companies"
              options={companies}
              value={barCompFilter === "all" ? "" : barCompFilter}
              onChange={val => setBarCompFilter(val || "all")}
              isClearable={true}
              style={{ minWidth: 200 }}
            />
          </div>
          <div className="filter-group">
            <CustomSelect 
              placeholder="All Departments"
              options={departments}
              value={barDeptFilter === "all" ? "" : barDeptFilter}
              onChange={val => setBarDeptFilter(val || "all")}
              isClearable={true}
              style={{ minWidth: 200 }}
            />
          </div>
          </div>
          <div className="chart-legend-horizontal">
             <div className="legend-item-horizontal"><div className="legend-dot" style={{ background: '#9333ea' }}></div><span>Total Tasks</span></div>
             <div className="legend-item-horizontal"><div className="legend-dot" style={{ background: '#22c55e' }}></div><span>Completed Tasks</span></div>
             <div className="legend-item-horizontal"><div className="legend-dot" style={{ background: '#eab308' }}></div><span>Pending Tasks</span></div>
             <div className="legend-item-horizontal"><div className="legend-dot" style={{ background: '#ef4444' }}></div><span>Overdue Tasks</span></div>
             <div className="legend-item-horizontal"><div className="legend-dot" style={{ background: '#06b6d4' }}></div><span>On Time</span></div>
             <div className="legend-item-horizontal"><div className="legend-dot" style={{ background: '#f97316' }}></div><span>Delayed</span></div>
          </div>
          <div className="grouped-bar-container">
             <div className="chart-y-axis-grouped">
                <span>{companyBarMax}</span>
                <span>{Math.round(companyBarMax * 0.75)}</span>
                <span>{Math.round(companyBarMax * 0.5)}</span>
                <span>{Math.round(companyBarMax * 0.25)}</span>
                <span>0</span>
             </div>
             <div className="grid-wrapper-grouped">
                {[1, 0.75, 0.5, 0.25, 0].map(v => <div key={v} style={{ borderTop: '1px solid #f1f5f9', width: '100%', height: 0 }}></div>)}
             </div>
             {companyDetailedStats.map(comp => (
                <div key={comp.name} className="bar-group">
                   <GroupBar value={comp.total} color="#9333ea" max={companyBarMax} label="Total" />
                   <GroupBar value={comp.completed} color="#22c55e" max={companyBarMax} label="Completed" />
                   <GroupBar value={comp.pending} color="#eab308" max={companyBarMax} label="Pending" />
                   <GroupBar value={comp.overdue} color="#ef4444" max={companyBarMax} label="Overdue" />
                   <GroupBar value={comp.withinTime} color="#06b6d4" max={companyBarMax} label="Within" />
                   <GroupBar value={comp.afterTime} color="#f97316" max={companyBarMax} label="After" />
                   <div className="group-title-label">{comp.name}</div>
                </div>
             ))}
          </div>
       </div>


      <div className="card">
        <div className="card-header">
          <span className="card-title">🕐 Recent Tasks</span>
          <button className="btn btn-secondary btn-sm" onClick={() => onNavigate("all-tasks")}>View All →</button>
        </div>
        <div className="table-wrapper incard-table">
          <table>
            <thead><tr>
              <th>Task ID</th><th>Task Name</th><th>Status</th>
              <th>Priority</th><th>Assigned To</th><th>Due Date</th>
            </tr></thead>
            <tbody>
              {recent.map((row,i) => {
                const f = row.fields || {};
                const { cls, label } = getStatusBadge(cellText(f["Overall Status"]));
                return (
                  <tr key={i}>
                    <td style={{ color:"var(--primary)", fontWeight:700, fontSize:12 }}>{cellText(f["Task ID"])||"—"}</td>
                    <td style={{ fontWeight:500 }}>{cellText(f["Task Name"])||"—"}</td>
                    <td><span className={`badge ${cls}`}>{label||"—"}</span></td>
                    <td>
                      {(() => {
                        const { bg, color, label } = getPriorityBadge(cellText(f["Priority"]));
                        return <span className="badge" style={{ background:bg, color:color }}>{label}</span>;
                      })()}
                    </td>
                    <td>{cellText(f["Assigned Users"])||cellText(f["Assigned By"])||"—"}</td>
                    <td style={{ fontSize:12, color:"var(--gray-500)" }}>{formatDate(f["End Date"])}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PieChart({ title, data, totalColor, typeLabel }) {
  const [hovered, setHovered] = React.useState(null);
  const total = data.reduce((sum, item) => sum + item.count, 0);
  const radius = 72;
  const strokeWidth = 24;
  const circ = 2 * Math.PI * radius;
  let offset = 0;
  const COLORS = ["#334155", "#f97316", "#9333ea", "#94a3b8", "#f43f5e", "#7c3aed", "#ec4899", "#2dd4bf", "#78716c", "#fbbf24", "#eab308", "#10b981", "#6366f1", "#06b6d4"];

  return (
    <div className="donut-card" style={{ borderTop: `4px solid ${totalColor}` }}>
      <div className="donut-title" style={{ color: totalColor }}>{title}</div>
      <div className="donut-wrapper">
        <svg viewBox="0 0 200 200" width="240" height="240" style={{ transform: 'rotate(-90deg)', overflow: 'visible' }}>
          {data.map((item, i) => {
            const pct = total > 0 ? (item.count / total) : 0;
            const dash = `${pct * circ} ${circ}`;
            const offs = -offset;
            offset += pct * circ;
            const color = item.color || COLORS[i % COLORS.length];
            const pctValue = (pct * 100).toFixed(1);
            return (
              <circle 
                key={i} 
                className="donut-segment" 
                cx="100" cy="100" r={radius} 
                fill="transparent"
                stroke={color} 
                strokeWidth={strokeWidth}
                strokeDasharray={dash} 
                strokeDashoffset={offs}
                onMouseEnter={() => setHovered({ ...item, pct: pctValue })}
                onMouseLeave={() => setHovered(null)}
                style={{ transition: 'all 0.3s ease' }}
              />
            );
          })}
          {total === 0 && <circle cx="100" cy="100" r={radius} fill="transparent" stroke="#f1f5f9" strokeWidth={strokeWidth} />}
        </svg>
        <div className="donut-center-text">
          <span className="donut-number">{total}</span>
          <span className="donut-label">{typeLabel || "Tasks"}</span>
        </div>
      </div>
      <div className="chart-legend" style={{ marginTop: '2rem' }}>
         {data.map((item, i) => (
           <div key={i} className="legend-item" onMouseEnter={() => setHovered({ ...item, pct: ((item.count/total)*100).toFixed(1) })} onMouseLeave={() => setHovered(null)}>
             <div className="legend-dot" style={{ background: item.color || COLORS[i % COLORS.length] }}></div>
             <span style={{ fontSize: '0.75rem', fontWeight: hovered?.name === item.name ? 700 : 400 }}>{item.name} ({item.count})<span style={{color: 'var(--gray-400)', marginLeft: '4px'}}>{((item.count/total)*100).toFixed(1)}%</span></span>
           </div>
         ))}
      </div>
    </div>
  );
}

function GroupBar({ value, color, max, label }) {
  const height = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="group-bar" title={`${label}: ${value}`} style={{ height: `${Math.max(height, 2)}%`, background: color }}>
       <div className="group-bar-label-value">{value}</div>
    </div>
  );
}
